import { useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";

// =============================================================================
// Animation Variants
// =============================================================================

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6 },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const scaleIn = {
  hidden: { scale: 0.9, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

// =============================================================================
// Data
// =============================================================================

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
  isActive?: boolean;
  isFuture?: boolean;
}

const TIMELINE_EVENTS: TimelineEvent[] = [
  {
    year: "2024",
    title: "Founded with Vision of Private AI",
    description:
      "HVME was born from a simple belief: AI should empower, not exploit. Our founding team set out to build infrastructure that makes privacy the default, not an afterthought.",
    isActive: false,
  },
  {
    year: "2025",
    title: "First FHE Implementation",
    description:
      "Breakthrough achievement in fully homomorphic encryption for AI workloads. We proved that secure computation at scale was not just possible, but practical.",
    isActive: false,
  },
  {
    year: "2026",
    title: "Production Launch",
    description:
      "HVME goes live. Enterprise-ready, battle-tested infrastructure now available to organizations worldwide. The era of private AI begins.",
    isActive: true,
  },
  {
    year: "Future",
    title: "Expanding the Ecosystem",
    description:
      "Building toward a world where private computation is ubiquitous. Open protocols, community governance, and decentralized infrastructure.",
    isFuture: true,
  },
];

interface CoreValue {
  title: string;
  tagline: string;
  description: string;
  icon: React.ReactNode;
}

const CORE_VALUES: CoreValue[] = [
  {
    title: "Privacy First",
    tagline: "Your data, your rules",
    description:
      "Every architectural decision starts with the question: does this protect user privacy? We build systems where data sovereignty is non-negotiable.",
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
  {
    title: "Open Source",
    tagline: "Transparent, auditable code",
    description:
      "Trust through transparency. Our cryptographic implementations are open for scrutiny. Security by obscurity is no security at all.",
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
      </svg>
    ),
  },
  {
    title: "Decentralized",
    tagline: "No single point of failure",
    description:
      "Distributed trust through threshold cryptography. No single entity holds the keys to your data. Resilience by design.",
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
      </svg>
    ),
  },
];

interface Partner {
  name: string;
  logo: string;
}

const PARTNERS: Partner[] = [
  { name: "Chainlink", logo: "LINK" },
  { name: "AWS", logo: "AWS" },
  { name: "Google Cloud", logo: "GCP" },
  { name: "Microsoft Azure", logo: "Azure" },
];

interface SocialLink {
  name: string;
  href: string;
  icon: React.ReactNode;
}

const SOCIAL_LINKS: SocialLink[] = [
  {
    name: "GitHub",
    href: "https://github.com/hvme",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
      </svg>
    ),
  },
  {
    name: "Twitter/X",
    href: "https://x.com/hvme",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    name: "Discord",
    href: "https://discord.gg/hvme",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
      </svg>
    ),
  },
  {
    name: "Telegram",
    href: "https://t.me/hvme",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
      </svg>
    ),
  },
  {
    name: "LinkedIn",
    href: "https://linkedin.com/company/hvme",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
];

// =============================================================================
// Hero Section
// =============================================================================

function HeroSection() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroY = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section ref={heroRef} className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
      {/* Background gradient orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{
            background: "radial-gradient(circle, rgba(220, 160, 250, 0.6), transparent 70%)",
          }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.25, 0.2],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl opacity-15"
          style={{
            background: "radial-gradient(circle, rgba(117, 142, 255, 0.6), transparent 70%)",
          }}
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.15, 0.2, 0.15],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <motion.div
        style={{ y: heroY, opacity: heroOpacity }}
        className="relative z-10 text-center px-6 max-w-4xl mx-auto"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {/* Mission statement */}
          <h1
            className="font-heading font-black text-5xl md:text-7xl tracking-tight mb-6"
            style={{
              background: "linear-gradient(135deg, #DCA0FA 0%, #E6BEFA 50%, #758EFF 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Making AI Private by Default
          </h1>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="font-body text-lg md:text-xl text-hvme-text-secondary max-w-2xl mx-auto leading-relaxed"
          >
            We envision a future where confidential computing is the standard, not the exception.
            Where AI serves humanity without harvesting its most intimate data.
            HVME is building that future, today.
          </motion.p>

          {/* Decorative element */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="mt-12 mx-auto w-24 h-px bg-gradient-to-r from-transparent via-hvme-primary to-transparent"
          />
        </motion.div>
      </motion.div>
    </section>
  );
}

// =============================================================================
// Timeline Section
// =============================================================================

function TimelineMarker({ isActive, isFuture, index }: { isActive?: boolean; isFuture?: boolean; index: number }) {
  return (
    <div className="relative flex items-center justify-center">
      {/* Outer ring with pulse for active */}
      {isActive && (
        <motion.div
          className="absolute w-8 h-8 rounded-full border-2 border-hvme-primary"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.6, 0, 0.6],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      )}

      {/* Main marker */}
      <motion.div
        initial={{ scale: 0 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.15, type: "spring", stiffness: 200 }}
        className={`
          relative w-4 h-4 rounded-full z-10
          ${isActive
            ? "bg-hvme-primary shadow-[0_0_20px_rgba(220,160,250,0.6)]"
            : isFuture
              ? "bg-hvme-bg-surface border-2 border-dashed border-hvme-primary/50"
              : "bg-hvme-primary/60"
          }
        `}
      >
        {/* Inner dot for active */}
        {isActive && (
          <motion.div
            className="absolute inset-1 rounded-full bg-white"
            animate={{ opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        )}
      </motion.div>
    </div>
  );
}

function TimelineSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section ref={sectionRef} className="py-24 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-hvme-text-primary mb-4">
            Our Journey
          </h2>
          <p className="font-body text-hvme-text-secondary max-w-xl mx-auto">
            From vision to reality, building the infrastructure for private AI.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <motion.div
            initial={{ scaleY: 0 }}
            animate={isInView ? { scaleY: 1 } : { scaleY: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px origin-top"
            style={{
              background: "linear-gradient(to bottom, rgba(220, 160, 250, 0.1), rgba(220, 160, 250, 0.4), rgba(220, 160, 250, 0.4), rgba(220, 160, 250, 0.1))",
            }}
          />

          {/* Timeline events */}
          {TIMELINE_EVENTS.map((event, index) => (
            <motion.div
              key={event.year}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className={`relative flex items-start mb-12 last:mb-0 ${
                index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
              }`}
            >
              {/* Marker positioned on the line */}
              <div className="absolute left-8 md:left-1/2 -translate-x-1/2">
                <TimelineMarker isActive={event.isActive} isFuture={event.isFuture} index={index} />
              </div>

              {/* Content card */}
              <div
                className={`ml-20 md:ml-0 md:w-[calc(50%-2rem)] ${
                  index % 2 === 0 ? "md:pr-8 md:text-right" : "md:pl-8 md:ml-[calc(50%+2rem)]"
                }`}
              >
                <motion.div
                  whileHover={{ y: -2, borderColor: "rgba(220, 160, 250, 0.3)" }}
                  className="p-6 rounded-2xl bg-hvme-bg-surface border border-hvme-border transition-all duration-300"
                >
                  {/* Year badge */}
                  <span
                    className={`inline-block px-3 py-1 mb-3 rounded-full text-xs font-mono border ${
                      event.isActive
                        ? "bg-hvme-primary/20 text-hvme-primary border-hvme-primary/30"
                        : event.isFuture
                          ? "bg-hvme-bg-mid text-hvme-text-muted border-hvme-border border-dashed"
                          : "bg-hvme-primary/10 text-hvme-primary/80 border-hvme-primary/20"
                    }`}
                  >
                    {event.year}
                  </span>

                  <h3 className="font-heading font-bold text-lg text-hvme-text-primary mb-2">
                    {event.title}
                  </h3>
                  <p className="font-body text-sm text-hvme-text-secondary leading-relaxed">
                    {event.description}
                  </p>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// Core Values Section
// =============================================================================

function CoreValueCard({ value, index }: { value: CoreValue; index: number }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      variants={fadeInUp}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ y: -6 }}
      className="relative p-8 rounded-3xl bg-hvme-bg-surface border border-hvme-border overflow-hidden transition-all duration-300 hover:border-hvme-primary/30 hover:shadow-[0_0_40px_rgba(220,160,250,0.1)]"
    >
      {/* Decorative gradient corner */}
      <motion.div
        className="absolute top-0 right-0 w-40 h-40 pointer-events-none"
        animate={{
          opacity: isHovered ? 0.25 : 0.1,
        }}
        transition={{ duration: 0.3 }}
        style={{
          background: "radial-gradient(circle at top right, rgba(220, 160, 250, 0.4), transparent 70%)",
        }}
      />

      {/* Icon */}
      <motion.div
        className="w-14 h-14 rounded-2xl bg-hvme-primary/10 border border-hvme-primary/20 flex items-center justify-center mb-6 text-hvme-primary"
        animate={{
          scale: isHovered ? 1.05 : 1,
          borderColor: isHovered ? "rgba(220, 160, 250, 0.4)" : "rgba(220, 160, 250, 0.2)",
        }}
        transition={{ duration: 0.3 }}
      >
        {value.icon}
      </motion.div>

      {/* Title */}
      <h3 className="font-heading font-bold text-xl text-hvme-text-primary mb-2">
        {value.title}
      </h3>

      {/* Tagline */}
      <p className="font-body text-sm text-hvme-primary font-medium mb-4">
        "{value.tagline}"
      </p>

      {/* Description */}
      <p className="font-body text-sm text-hvme-text-secondary leading-relaxed">
        {value.description}
      </p>
    </motion.div>
  );
}

function CoreValuesSection() {
  return (
    <section className="py-24 px-6 bg-hvme-bg-mid">
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-hvme-text-primary mb-4">
            Our Core Values
          </h2>
          <p className="font-body text-hvme-text-secondary max-w-xl mx-auto">
            The principles that guide every line of code we write.
          </p>
        </motion.div>

        {/* Values grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {CORE_VALUES.map((value, index) => (
            <CoreValueCard key={value.title} value={value} index={index} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// =============================================================================
// Technology Highlights Section
// =============================================================================

function FHEDiagram() {
  const diagramRef = useRef(null);
  const isInView = useInView(diagramRef, { once: true, margin: "-50px" });

  return (
    <div ref={diagramRef} className="relative w-full max-w-md mx-auto">
      {/* Central FHE node */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="relative mx-auto w-32 h-32 rounded-full bg-gradient-to-br from-hvme-primary to-hvme-accent flex items-center justify-center shadow-[0_0_60px_rgba(220,160,250,0.3)]"
      >
        <span className="font-heading font-bold text-lg text-hvme-bg-deep">FHE</span>

        {/* Orbiting ring */}
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-dashed border-hvme-primary/30"
          style={{ transform: "scale(1.5)" }}
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
      </motion.div>

      {/* Data flow arrows */}
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Left: Encrypted Input */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="absolute left-0 top-1/2 -translate-y-1/2 text-center"
        >
          <div className="w-16 h-16 rounded-xl bg-hvme-bg-surface border border-hvme-border flex items-center justify-center mb-2">
            <svg className="w-6 h-6 text-hvme-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>
          <span className="text-xs text-hvme-text-muted">Encrypted</span>
        </motion.div>

        {/* Right: Encrypted Output */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="absolute right-0 top-1/2 -translate-y-1/2 text-center"
        >
          <div className="w-16 h-16 rounded-xl bg-hvme-bg-surface border border-hvme-border flex items-center justify-center mb-2">
            <svg className="w-6 h-6 text-hvme-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          </div>
          <span className="text-xs text-hvme-text-muted">Secure</span>
        </motion.div>

        {/* Top: AI Processing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="absolute top-0 left-1/2 -translate-x-1/2 text-center"
        >
          <div className="w-16 h-16 rounded-xl bg-hvme-bg-surface border border-hvme-border flex items-center justify-center mb-2">
            <svg className="w-6 h-6 text-hvme-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
            </svg>
          </div>
          <span className="text-xs text-hvme-text-muted">AI Compute</span>
        </motion.div>
      </div>
    </div>
  );
}

function TechnologySection() {
  const statsRef = useRef(null);
  const isInView = useInView(statsRef, { once: true, margin: "-50px" });

  const performanceMetrics = [
    { label: "Encryption Overhead", value: "<10ms", description: "Per operation" },
    { label: "Throughput", value: "10K+", description: "Operations/sec" },
    { label: "Key Size", value: "256-bit", description: "Post-quantum secure" },
  ];

  return (
    <section className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-hvme-text-primary mb-4">
            Technology Highlights
          </h2>
          <p className="font-body text-hvme-text-secondary max-w-xl mx-auto">
            Cutting-edge cryptography meets practical engineering.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* FHE Explanation */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="font-heading font-bold text-2xl text-hvme-text-primary mb-4">
              Fully Homomorphic Encryption
            </h3>
            <p className="font-body text-hvme-text-secondary mb-6 leading-relaxed">
              FHE allows computations on encrypted data without ever decrypting it.
              Your data enters encrypted, is processed encrypted, and leaves encrypted.
              Even we cannot see what you're computing.
            </p>

            {/* Post-quantum badge */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-hvme-bg-surface border border-hvme-border"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-hvme-primary/20 to-hvme-accent/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-hvme-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                </svg>
              </div>
              <div>
                <p className="font-heading font-bold text-sm text-hvme-text-primary">Post-Quantum Secure</p>
                <p className="text-xs text-hvme-text-muted">Lattice-based cryptography</p>
              </div>
              <motion.div
                className="ml-2 px-2 py-0.5 rounded-full bg-hvme-green/10 text-hvme-green text-xs font-medium"
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                Active
              </motion.div>
            </motion.div>
          </motion.div>

          {/* FHE Diagram */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center py-12"
          >
            <FHEDiagram />
          </motion.div>
        </div>

        {/* Performance Metrics */}
        <motion.div
          ref={statsRef}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16"
        >
          {performanceMetrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              variants={scaleIn}
              className="p-6 rounded-2xl bg-hvme-bg-surface border border-hvme-border text-center"
            >
              <p className="font-heading font-black text-3xl text-hvme-primary mb-1">
                {metric.value}
              </p>
              <p className="font-heading font-medium text-sm text-hvme-text-primary mb-1">
                {metric.label}
              </p>
              <p className="text-xs text-hvme-text-muted">{metric.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// =============================================================================
// Partners/Backers Section
// =============================================================================

function PartnersSection() {
  return (
    <section className="py-24 px-6 bg-hvme-bg-mid">
      <div className="max-w-5xl mx-auto">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-hvme-text-primary mb-4">
            Backed By Industry Leaders
          </h2>
          <p className="font-body text-hvme-text-secondary max-w-xl mx-auto">
            Supported by visionaries who believe in the future of private AI.
          </p>
        </motion.div>

        {/* Logo grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
        >
          {PARTNERS.map((partner, index) => (
            <motion.div
              key={partner.name}
              variants={fadeIn}
              whileHover={{ scale: 1.05 }}
              className="group relative aspect-[3/2] rounded-xl bg-hvme-bg-surface border border-hvme-border flex items-center justify-center transition-all duration-300 hover:border-hvme-primary/30 hover:shadow-[0_0_30px_rgba(220,160,250,0.1)] cursor-default overflow-hidden"
            >
              {/* Grayscale to color reveal */}
              <span
                className="font-heading font-bold text-xl transition-all duration-500 grayscale group-hover:grayscale-0"
                style={{
                  color: "rgba(220, 160, 250, 0.4)",
                }}
              >
                <span className="group-hover:text-hvme-primary transition-colors duration-300">
                  {partner.logo}
                </span>
              </span>

              {/* Hover glow */}
              <motion.div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{
                  background: "radial-gradient(circle at center, rgba(220, 160, 250, 0.1), transparent 70%)",
                }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// =============================================================================
// Contact Section
// =============================================================================

function ContactSection() {
  return (
    <section className="py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative p-12 rounded-3xl bg-hvme-bg-surface border border-hvme-border overflow-hidden text-center"
        >
          {/* Background decoration */}
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-10"
              style={{
                background: "radial-gradient(circle, rgba(220, 160, 250, 0.8), transparent 70%)",
              }}
            />
            <div
              className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full blur-3xl opacity-10"
              style={{
                background: "radial-gradient(circle, rgba(117, 142, 255, 0.8), transparent 70%)",
              }}
            />
          </div>

          <div className="relative z-10">
            <h2 className="font-heading font-bold text-3xl text-hvme-text-primary mb-4">
              Get in Touch
            </h2>
            <p className="font-body text-hvme-text-secondary mb-8 max-w-lg mx-auto">
              Have questions about HVME? Want to explore partnership opportunities?
              We'd love to hear from you.
            </p>

            {/* Email */}
            <motion.a
              href="mailto:contact@hvme.ai"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-hvme-primary/10 border border-hvme-primary/20 text-hvme-primary font-medium transition-all duration-300 hover:bg-hvme-primary/20 hover:border-hvme-primary/40 mb-8"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
              contact@hvme.ai
            </motion.a>

            {/* Social links */}
            <div className="flex items-center justify-center gap-4 mb-8">
              {SOCIAL_LINKS.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-12 h-12 rounded-xl bg-hvme-bg-mid border border-hvme-border flex items-center justify-center text-hvme-text-secondary transition-all duration-300 hover:text-hvme-primary hover:border-hvme-primary/30"
                  aria-label={social.name}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>

            {/* Office location (optional) */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="flex items-center justify-center gap-2 text-sm text-hvme-text-muted"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
              </svg>
              <span>San Francisco, CA / Remote-First</span>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// =============================================================================
// Main About Page Component
// =============================================================================

export function AboutPage() {
  return (
    <div className="min-h-screen">
      <HeroSection />
      <TimelineSection />
      <CoreValuesSection />
      <TechnologySection />
      <PartnersSection />
      <ContactSection />
    </div>
  );
}

export default AboutPage;
